'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useData } from '@/components';
import { useEcomStoreSelector } from '@/data';
import { Store, TreezStore } from '@/types';
import { filterStoreContent, resolveTimeZone } from '@/utils';

const reverseProxyLink = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

const withDispensaryTimeZone = (
  store: Store,
  selectedTreez: TreezStore | undefined | null,
): Store => ({
  ...store,
  timezone: resolveTimeZone(selectedTreez?.dispensary_time_zone),
});

const SCHEDULE_KEYS = [
  'pickup_schedules',
  'delivery_schedules',
  'curbside_schedules',
] as const;

/** Compare only schedule data to avoid re-renders when refetched store is identical. */
function schedulesEqual(
  a: Store | null | undefined,
  b: Store | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return SCHEDULE_KEYS.every(
    key => JSON.stringify(a[key]) === JSON.stringify(b[key]),
  );
}

type OrderLike = {
  delivery_details?: { pickup?: boolean | null } | null;
  delivery_address?: unknown;
};

/**
 * Hook for checkout page: provides gapCommerceStore (same mapping as page)
 * and refetchStoreSchedules to fetch fresh store/schedules from the server
 * without touching GetStoreMetadata or the existing ssr-cache data flow.
 * Used by the checkout lib to validate time slots against updated schedules
 * after an admin revalidate.
 */
export function useCheckoutStore(order: OrderLike | undefined) {
  const { store: gcStore, treezStores } = useData();
  const { treez_store_name, provider_inventory_location_id } =
    useEcomStoreSelector([
      'treez_store_name',
      'provider_inventory_location_id',
    ]);

  const selectedTreez =
    treezStores?.find((s: TreezStore) => s?.short_name === treez_store_name) ??
    null;

  const gcStoreWithGeoInfo = selectedTreez
    ? {
        ...gcStore,
        address1: selectedTreez?.address1,
        address2: selectedTreez?.address2,
        city: selectedTreez?.city,
        country: selectedTreez?.country,
        province_code: selectedTreez?.state,
        zip: selectedTreez?.zip,
        latitude: selectedTreez?.geoloc?.lat,
        longitude: selectedTreez?.geoloc?.lng,
        phone_number: selectedTreez?.phone,
      }
    : gcStore;

  const filteredStore: Store = filterStoreContent(
    gcStoreWithGeoInfo as Store,
    selectedTreez?.short_name,
    order?.delivery_details?.pickup ?? undefined,
    order?.delivery_address ?? undefined,
    !!provider_inventory_location_id,
    treezStores,
  );

  const gapCommerceStore: Store = withDispensaryTimeZone(
    filteredStore,
    selectedTreez,
  );

  // After refetch, use this store so checkout UI shows updated slots; cleared when store selection changes
  const [refetchedStore, setRefetchedStore] = useState<Store | null>(null);
  const effectiveStoreRef = useRef<Store>(gapCommerceStore);
  effectiveStoreRef.current = refetchedStore ?? gapCommerceStore;

  useEffect(() => {
    setRefetchedStore(null);
  }, [treez_store_name]);

  const refetchStoreSchedules = useCallback(async (): Promise<Store | void> => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}${reverseProxyLink}/api/ssr-cache`;
    try {
      const res = await fetch(url, {
        cache: 'no-store',
      });
      if (!res.ok) return;

      const data = await res.json();
      const cachedStore = data?.store as Store | undefined;
      const cachedTreezStores = (data?.treezStores ?? []) as TreezStore[];

      if (!cachedStore || !cachedTreezStores?.length) return;

      const selected =
        cachedTreezStores.find(
          (s: TreezStore) => s?.short_name === treez_store_name,
        ) ?? null;
      if (!selected) return cachedStore;

      const gcWithGeo = {
        ...cachedStore,
        address1: selected?.address1,
        address2: selected?.address2,
        city: selected?.city,
        country: selected?.country,
        province_code: selected?.state,
        zip: selected?.zip,
        latitude: selected?.geoloc?.lat,
        longitude: selected?.geoloc?.lng,
        phone_number: selected?.phone,
      };

      const filtered = filterStoreContent(
        gcWithGeo as Store,
        selected?.short_name,
        order?.delivery_details?.pickup ?? undefined,
        order?.delivery_address ?? undefined,
        !!provider_inventory_location_id,
        treezStores,
      );
      const result: Store = withDispensaryTimeZone(filtered, selected);
      if (!schedulesEqual(result, effectiveStoreRef.current))
        setRefetchedStore(result);
      return result;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('error while refetching store schedules', err);
      return;
    }
  }, [
    order?.delivery_details?.pickup,
    order?.delivery_address,
    treez_store_name,
    provider_inventory_location_id,
  ]);

  return {
    gapCommerceStore: refetchedStore ?? gapCommerceStore,
    refetchStoreSchedules,
  };
}
